import React, { useState } from "react";
import { EyeIcon, EyeOffIcon, Spinner } from "./Shared/Icons";
import type { useRegister } from "../hooks/useRegister";

interface RegisterViewProps {
  register: ReturnType<typeof useRegister>;
  isDark: boolean;
  onCancel: () => void;
}

export const RegisterView: React.FC<RegisterViewProps> = ({
  register,
  isDark,
  onCancel,
}) => {
  const {
    name,
    email,
    password,
    confirmPassword,
    code,
    error,
    successMessage,
    isLoading,
    step,
    setName,
    setEmail,
    setPassword,
    setConfirmPassword,
    setCode,
    setError,
    setStep,
    handleSignUp,
    handleConfirmSignUp,
    handleResendCode,
  } = register;

  const [showRegPass, setShowRegPass] = useState(false);
  const [showRegConfirmPass, setShowRegConfirmPass] = useState(false);

  return (
    <div>
      <p className={`text-xs text-center leading-relaxed mb-3 ${isDark ? "text-white/40" : "text-gray-500"}`}>
        {step === "SIGNUP"
          ? "Completá los datos para registrarte"
          : `Ingresá el código de verificación enviado a ${email}`}
      </p>

      {error && (
        <div className="flex items-center gap-2 px-4 py-3 bg-rose-500/10 border border-rose-500/20 text-rose-400 rounded-xl text-xs font-semibold mb-4 leading-normal">
          <span>⚠️</span>
          <span>{error}</span>
        </div>
      )}

      {successMessage && (
        <div className="flex items-center gap-2 px-4 py-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-xl text-xs font-semibold mb-4 leading-normal">
          <span>✅</span>
          <span>{successMessage}</span>
        </div>
      )}

      {step === "SIGNUP" ? (
        <form onSubmit={handleSignUp} className="flex flex-col gap-4">
          <input
            type="text"
            placeholder="Nombre Completo"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isLoading}
            className={`w-full py-4 px-5 border rounded-xl text-xs font-medium transition-all outline-none ${
              isDark
                ? "bg-white/[0.03] border-white/10 text-white placeholder-white/20 focus:border-[#058b95] focus:bg-[#121214]"
                : "bg-[#F5F7F4] border-transparent text-[#212121] placeholder-gray-400 focus:border-[#058b95] focus:bg-white"
            }`}
            autoFocus
          />

          <input
            type="email"
            placeholder="Correo electrónico"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
            className={`w-full py-4 px-5 border rounded-xl text-xs font-medium transition-all outline-none ${
              isDark
                ? "bg-white/[0.03] border-white/10 text-white placeholder-white/20 focus:border-[#058b95] focus:bg-[#121214]"
                : "bg-[#F5F7F4] border-transparent text-[#212121] placeholder-gray-400 focus:border-[#058b95] focus:bg-white"
            }`}
          />

          {/* Password */}
          <div className="relative">
            <input
              type={showRegPass ? "text" : "password"}
              placeholder="Contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              className={`w-full py-4 px-5 border rounded-xl text-xs font-medium transition-all outline-none ${
                isDark
                  ? "bg-white/[0.03] border-white/10 text-white placeholder-white/20 focus:border-[#058b95] focus:bg-[#121214]"
                  : "bg-[#F5F7F4] border-transparent text-[#212121] placeholder-gray-400 focus:border-[#058b95] focus:bg-white"
              }`}
            />
            <button
              type="button"
              onClick={() => setShowRegPass(!showRegPass)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#058b95] transition-all text-xs flex items-center justify-center"
            >
              {showRegPass ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>

          {/* Confirm Password */}
          <div className="relative">
            <input
              type={showRegConfirmPass ? "text" : "password"}
              placeholder="Confirmar contraseña"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isLoading}
              className={`w-full py-4 px-5 border rounded-xl text-xs font-medium transition-all outline-none ${
                isDark
                  ? "bg-white/[0.03] border-white/10 text-white placeholder-white/20 focus:border-[#058b95] focus:bg-[#121214]"
                  : "bg-[#F5F7F4] border-transparent text-[#212121] placeholder-gray-400 focus:border-[#058b95] focus:bg-white"
              }`}
            />
            <button
              type="button"
              onClick={() => setShowRegConfirmPass(!showRegConfirmPass)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#058b95] transition-all text-xs flex items-center justify-center"
            >
              {showRegConfirmPass ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-4 bg-[#058b95] hover:bg-[#04727a] disabled:opacity-60 text-white font-bold rounded-xl text-sm transition-all shadow-lg shadow-cyan-500/20 active:scale-[0.98] flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Spinner />
                <span>Registrando...</span>
              </>
            ) : (
              "Registrarse"
            )}
          </button>

          <button
            type="button"
            onClick={onCancel}
            className={`text-xs font-semibold text-center mt-0 ${isDark ? "text-white/60 hover:text-white" : "text-gray-600 hover:text-[#058b95]"}`}
          >
            ¿Ya tenés cuenta? Iniciar sesión
          </button>
        </form>
      ) : (
        <form onSubmit={(e) => handleConfirmSignUp(e, onCancel)} className="flex flex-col gap-4">
          <input
            type="text"
            placeholder="Código de verificación"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={isLoading}
            className={`w-full py-4 px-5 border rounded-xl text-xs font-medium transition-all outline-none ${
              isDark
                ? "bg-white/[0.03] border-white/10 text-white placeholder-white/20 focus:border-[#058b95] focus:bg-[#121214]"
                : "bg-[#F5F7F4] border-transparent text-[#212121] placeholder-gray-400 focus:border-[#058b95] focus:bg-white"
            }`}
            autoFocus
          />

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-4 bg-[#058b95] hover:bg-[#04727a] disabled:opacity-60 text-white font-bold rounded-xl text-sm transition-all shadow-lg shadow-cyan-500/20 active:scale-[0.98] flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Spinner />
                <span>Confirmando...</span>
              </>
            ) : (
              "Confirmar cuenta"
            )}
          </button>
          <button
            type="button"
            onClick={() => {
              setStep("SIGNUP");
              setError("");
            }}
            className={`text-xs font-semibold text-center mt-1 ${isDark ? "text-white/60 hover:text-white" : "text-gray-600 hover:text-[#058b95]"}`}
          >
            Volver al registro
          </button>
        </form>
      )}
    </div>
  );
};
